import { ChangeEvent, useMemo, useState } from 'react';
import { CommonValidation } from '../../common/commonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { BankDetailsErrors } from './BankDetailsForm';

type SortCodePart = {
    key: string;
    value: string;
};
type BankDetails = {
    accountHolderName: string;
    sortCode: SortCodePart[];
    accountNumber: string;
    paymentDay: string;
};

export function useBankDetails() {
    const { t } = useTranslation();

    const [bankDetails, setBankDetails] = useState<BankDetails>({
        accountHolderName: '',
        sortCode: ['1', '2', '3'].map((key) => ({
            key,
            value: ''
        })),
        accountNumber: '',
        paymentDay: '31'
    });

    const onChange = (e: ChangeEvent<HTMLInputElement>, key: string) => {
        if (bankDetails[key as keyof BankDetails] === e.currentTarget.value) {
            return;
        }
        setBankDetails({ ...bankDetails, [key]: e.currentTarget.value });
    };

    const onPaymentDayChange = (paymentDay: string) => {
        setBankDetails({ ...bankDetails, paymentDay });
    };

    const onSortCodeChange = (sortCode: SortCodePart[]) => {
        setBankDetails({ ...bankDetails, sortCode });
    };

    const daysOptions = useMemo(
        () =>
            Array.from({ length: 31 }, (_, i) => {
                const day = i + 1;
                const stringDay = day.toString();
                return {
                    id: stringDay,
                    text: stringDay,
                    value: day
                };
            }),
        []
    );

    const validateBankDetails = () => {
        const validationErrors: BankDetailsErrors = {};
        let isValid = true;
        const accountNumberLength = 8;

        if (!CommonValidation.isEmpty(bankDetails.accountNumber)) {
            if (!CommonValidation.isValidNumeric(bankDetails.accountNumber)) {
                validationErrors.accountNumber = t(
                    'direct_debit_guarantee.account_number_invalid'
                );
                isValid = false;
            } else if (
                bankDetails.accountNumber?.toString().length !==
                accountNumberLength
            ) {
                validationErrors.accountNumber = t(
                    'direct_debit_guarantee.account_number_length',
                    { accountNumberLength }
                );
                isValid = false;
            }
        } else {
            validationErrors.accountNumber = t(
                'direct_debit_guarantee.account_number_required'
            );
            isValid = false;
        }

        return { isValid, validationErrors };
    };

    return {
        bankDetails,
        daysOptions,
        onChange,
        onPaymentDayChange,
        onSortCodeChange,
        validateBankDetails
    };
}
